import { atom, selectorFamily } from "recoil";
import { ProgramContentType } from "data/BlockComponent";
import type { AutoFillStreamState } from "hooks/useAutoFillStream";

// 웹 자동 채우기 실행 — 활동지(생각기록지는 기록)마다 하나. 활동지를 나가도 남고, 넣거나 닫으면 지운다 (hooks/useAutoFillRuns).
// 메모리에만 있다 — 새로고침하면 사라진다. 앱은 화면 상태로 돈다 (useAutoFillStream)
export type AutoFillRun = AutoFillStreamState & {
  key: string;
  taskKey: string;
  thoughtRecordKey?: string;
  title: string;
  startedAt: number;
  // 요청을 만든 시점의 내용 — 칸 키가 이 배치 기준이라, 넣을 때 그때 내용과 구조를 비교한다
  baseContent: ProgramContentType[];
};

export const getAutoFillRunKey = (
  taskKey: string,
  thoughtRecordKey?: string
) => (thoughtRecordKey ? `${taskKey}|${thoughtRecordKey}` : taskKey);

export const autoFillRunsAtom = atom<Record<string, AutoFillRun>>({
  key: "autoFillRunsAtom",
  default: {},
});

// 지금 화면에 열린 활동지의 실행 key — 끝났을 때 이 화면이면 바로 넣고, 아니면 알림으로 알린다
export const autoFillViewingAtom = atom<string | undefined>({
  key: "autoFillViewingAtom",
  default: undefined,
});

// 오른쪽 아래 진행 표시 펼침
export const autoFillDockOpenAtom = atom<boolean>({
  key: "autoFillDockOpenAtom",
  default: false,
});

// 진행 표시 위 알림 (8초).
// filled: 이 화면에서 넣음, ready: 다른 화면에서 다 씀(열면 넣음), error: 다른 화면에서 실패
export type AutoFillToast = {
  id: number;
  runKey: string;
  title: string;
  message: string;
  kind: "filled" | "ready" | "error";
};

export const autoFillToastsAtom = atom<AutoFillToast[]>({
  key: "autoFillToastsAtom",
  default: [],
});

export const isAutoFillRunning = (run?: AutoFillRun) =>
  run?.phase === "running" || run?.phase === "canceling";

export const autoFillRunSelector = selectorFamily<
  AutoFillRun | undefined,
  string
>({
  key: "autoFillRunSelector",
  get:
    (key) =>
    ({ get }) =>
      get(autoFillRunsAtom)[key],
});

// 채우는 중이라 칸 입력·마치기를 막는지 — 진행 이벤트마다 다시 그리지 않게 boolean 으로
export const isAutoFillLockedSelector = selectorFamily<boolean, string>({
  key: "isAutoFillLockedSelector",
  get:
    (key) =>
    ({ get }) =>
      isAutoFillRunning(get(autoFillRunsAtom)[key]),
});

// 좌측 목록 — 그 활동지를 채우는 중인지. 생각기록지는 기록 key 로 찾는다 (생각기록지를 여는 활동지 포함)
export const isAutoFillRunningSelector = selectorFamily<
  boolean,
  { taskKey: string; thoughtRecordKey: string }
>({
  key: "isAutoFillRunningSelector",
  get:
    ({ taskKey, thoughtRecordKey }) =>
    ({ get }) =>
      Object.values(get(autoFillRunsAtom)).some(
        (run) =>
          isAutoFillRunning(run) &&
          (thoughtRecordKey
            ? run.thoughtRecordKey === thoughtRecordKey
            : !run.thoughtRecordKey && run.taskKey === taskKey)
      ),
});
